import { PHYSICS } from '../config.ts';
import { vec2, type Vec2 } from '../math/vec2.ts';
import { collideCircle, type Collider } from './Collision.ts';
import { COVERAGE_SECTORS, type FluidBallProbe } from './Ferrofluid.ts';
import type { MagneticField } from './MagneticField.ts';

/**
 * The ball is a plain rigid circle. It is deliberately *not* magnetic: the whole point of
 * the design is that the player moves it by moving fluid, not by pointing a magnet at it.
 *
 * Seen from above there is no gravity in the plane, so the ball only ever moves because
 * fluid pushed it or dragged it — and rolling friction is what brings it back to rest.
 *
 * The coating hook (PHYSICS.ballCoatingEnabled) is the planned exception — coat the ball
 * in enough ferrofluid and it starts responding to the field itself. The coverage metric
 * it needs is computed every tick already; only the coupling is switched off.
 */

/**
 * Contacts are summed over a reference count so the shove the ball feels does not scale
 * with however many particles happen to be touching it.
 */
const REFERENCE_CONTACTS = 24;

const scratchField = vec2();

export class Ball {
  readonly position: Vec2;
  readonly velocity: Vec2;
  readonly previousPosition: Vec2;
  radius: number;

  /** Fraction of the ball's circumference in contact with fluid, 0..1. */
  fluidCoverage = 0;
  /** Cosmetic only — the simulation has no angular dynamics. */
  spin = 0;
  /** Speed into the hardest wall hit this step, in wu/s. Zero when nothing was hit. */
  impactSpeed = 0;

  constructor(x: number, y: number, radius: number) {
    this.position = vec2(x, y);
    this.previousPosition = vec2(x, y);
    this.velocity = vec2(0, 0);
    this.radius = radius;
  }

  reset(x: number, y: number): void {
    this.position.x = x;
    this.position.y = y;
    this.previousPosition.x = x;
    this.previousPosition.y = y;
    this.velocity.x = 0;
    this.velocity.y = 0;
    this.fluidCoverage = 0;
    this.spin = 0;
    this.impactSpeed = 0;
  }

  step(
    dt: number,
    colliders: readonly Collider[],
    probe: FluidBallProbe | null,
    field: MagneticField,
  ): void {
    this.previousPosition.x = this.position.x;
    this.previousPosition.y = this.position.y;

    this.fluidCoverage = probe ? coverageFromSectors(probe.sectors) : 0;

    let ax = 0;
    let ay = PHYSICS.gravity;

    if (probe && probe.contacts > 0) {
      const contactScale = 1 / REFERENCE_CONTACTS;

      // Contact pressure: fluid piled against one side shoves the ball off it.
      ax += probe.pushX * PHYSICS.fluidBallForce * contactScale;
      ay += probe.pushY * PHYSICS.fluidBallForce * contactScale;

      // Viscous drag towards the fluid's own motion. This is the term that makes a blob
      // sliding across the table actually carry the ball with it, and the main way the
      // player steers: park a magnet, let the blob stream past the ball, and it goes too.
      const drag = PHYSICS.fluidBallDrag * this.fluidCoverage;
      ax += (probe.fluidVelX - this.velocity.x) * drag;
      ay += (probe.fluidVelY - this.velocity.y) * drag;
    }

    // Hook for milestone 2+: a fully coated ball couples to the field directly.
    if (PHYSICS.ballCoatingEnabled && this.fluidCoverage >= PHYSICS.ballCoatingThreshold) {
      field.sampleForce(this.position.x, this.position.y, scratchField);
      const coupling = PHYSICS.ballCoatingStrength * this.fluidCoverage;
      ax += scratchField.x * coupling;
      ay += scratchField.y * coupling;
    }

    // Rolling friction, plus extra damping while buried in fluid.
    const damping = PHYSICS.ballDrag + PHYSICS.fluidBallDamping * this.fluidCoverage;
    ax -= this.velocity.x * damping;
    ay -= this.velocity.y * damping;

    this.velocity.x += ax * dt;
    this.velocity.y += ay * dt;
    this.position.x += this.velocity.x * dt;
    this.position.y += this.velocity.y * dt;

    this.resolveColliders(colliders);
  }

  private resolveColliders(colliders: readonly Collider[]): void {
    const restitution = PHYSICS.ballRestitution;
    const friction = PHYSICS.ballFriction;
    this.impactSpeed = 0;

    // Two passes so the ball settles into corners instead of jittering between faces.
    for (let pass = 0; pass < 2; pass++) {
      for (let c = 0; c < colliders.length; c++) {
        const contact = collideCircle(colliders[c], this.position.x, this.position.y, this.radius);
        if (!contact) continue;

        const { nx, ny, depth } = contact;
        this.position.x += nx * depth;
        this.position.y += ny * depth;

        const normalSpeed = this.velocity.x * nx + this.velocity.y * ny;
        if (normalSpeed < 0) {
          this.impactSpeed = Math.max(this.impactSpeed, -normalSpeed);
          const tangentX = this.velocity.x - nx * normalSpeed;
          const tangentY = this.velocity.y - ny * normalSpeed;
          this.velocity.x = tangentX * friction - nx * normalSpeed * restitution;
          this.velocity.y = tangentY * friction - ny * normalSpeed * restitution;

          // Rolling look: spin follows the tangential slide. Cosmetic only.
          this.spin += (tangentX * -ny + tangentY * nx) / Math.max(this.radius, 1) * 0.016;
        }
      }
    }
  }
}

/** Fraction of the ball's circumference covered, from the fluid's sector bitmask. */
export function coverageFromSectors(sectors: number): number {
  let bits = 0;
  let mask = sectors;
  while (mask !== 0) {
    mask &= mask - 1;
    bits++;
  }
  return bits / COVERAGE_SECTORS;
}
